import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    canScanDocuments,
    listDocumentReaders,
    selectDocumentReader,
    type DocumentReaderState,
} from '../domain/documentScan';
import {
    middlewareIsUncertain,
    parseEidMiddleware,
    type EidMiddlewareReadiness,
} from '../domain/eidMiddleware';
import {
    canReadEid,
    listChipReaders,
    selectChipReader,
    type ChipReaderListing,
} from '../domain/eidReader';
import {
    chooseChipDevice,
    chooseOpticalDevice,
    defaultIdentityProvider,
    deriveChipAvailability,
    deriveOpticalAvailability,
    type ChipDeviceChoice,
    type IdentityProvider,
    type IdentityProviderAvailability,
    type OpticalDeviceChoice,
} from '../domain/identityReaderSelection';
import { probeHardwareService, type HardwareServiceStatus } from '../domain/labelPrinting';

/**
 * What this workstation can read an identity document with, and which reader it would use.
 *
 * One hook for both providers because they answer the same question for the same launcher, and two
 * separate probes would let the button and the modal disagree about what is attached.
 *
 * All the rules live in `domain/identityReaderSelection`; this holds only the React plumbing and the
 * operator's in-session choice.
 */
export interface UseIdentityReaderSelection {
    status: HardwareServiceStatus | null;
    chip: IdentityProviderAvailability;
    optical: IdentityProviderAvailability;
    /** The provider a launcher should open on, or null when neither can be used. */
    preferred: IdentityProvider | null;
    /** Which optical reader a scan would name, and whether a person still has to say. */
    device: OpticalDeviceChoice;
    /** Which chip reader a read would use, and whether a person still has to say. */
    chipDevice: ChipDeviceChoice;
    /**
     * What the workstation says about the national eID middleware, or null when it does not say.
     *
     * Null on any service older than the readiness probe, and that silence is never treated as a
     * problem — it simply leaves the chip rules exactly as they were.
     */
    middleware: EidMiddlewareReadiness | null;
    /** Whether to warn that middleware readiness could not be verified. Never disables anything. */
    middlewareUncertain: boolean;
    loading: boolean;
    busy: boolean;
    /** Picks an optical reader for this session only. Does not ask the service to remember it. */
    choose: (deviceId: string | null) => void;
    /**
     * Records which chip reader this workstation uses.
     *
     * Unlike the optical choice there is no session-only form of this, because a card read names no
     * device: the service is the only thing that can act on the answer, so a choice that stayed in
     * the page would leave the operator picking a reader and then watching the read fail anyway.
     */
    chooseChip: (deviceId: string | null) => Promise<boolean>;
    /**
     * Asks the service to remember a reader for this workstation.
     *
     * Stored by the service, in its own protected state. Never in the browser: local storage is
     * per-origin, per-profile and cleared with browsing data, which would silently return a
     * two-reader counter to "choose again" in the middle of a shift.
     */
    remember: (deviceId: string | null) => Promise<boolean>;
    reload: () => Promise<void>;
}

/** How often an open launcher re-reads the workstation. */
const RECONCILE_INTERVAL_MS = 20_000;

/** The page's own fetch, bound once so the domain modules stay free of globals. */
const browserFetch = (input: string, init?: RequestInit) => window.fetch(input, init);

export function useIdentityReaderSelection(options: {
    enabled: boolean;
}): UseIdentityReaderSelection {
    const { enabled } = options;

    const [status, setStatus] = useState<HardwareServiceStatus | null>(null);
    const [readers, setReaders] = useState<DocumentReaderState | null>(null);
    const [chipReaders, setChipReaders] = useState<ChipReaderListing | null>(null);
    const [chosen, setChosen] = useState<string | null>(null);
    const [chosenChip, setChosenChip] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    // A reconcile tick and a manual reload can land together, and a launcher that closed while one
    // was in flight must not have its state written afterwards.
    const live = useRef(true);

    useEffect(() => {
        live.current = true;

        return () => {
            live.current = false;
        };
    }, []);

    const reload = useCallback(async () => {
        setLoading(true);

        try {
            const probed = await probeHardwareService(browserFetch);

            if (!live.current) {
                return;
            }

            setStatus(probed);

            // Only a service that says it can do the thing is asked about its hardware. Anything
            // else would be a call this build is known to answer with 501, on a timer, for as long
            // as a page is open.
            //
            // Together rather than one after the other: they are two independent questions about
            // one workstation, and serialising them would make the second provider's state
            // arrive a round trip later than it needs to for no benefit.
            const [documents, chips] = await Promise.all([
                canScanDocuments(probed) ? listDocumentReaders(browserFetch) : null,
                canReadEid(probed) ? listChipReaders(browserFetch) : null,
            ]);

            if (live.current) {
                setReaders(documents?.ok === true ? documents.value : null);
                // Kept as the listing rather than unwrapped: a listing that failed and a
                // workstation with nothing attached are different things to tell an operator, and
                // reducing both to null here would throw that away before the rules see it.
                setChipReaders(chips);
            }
        } finally {
            if (live.current) {
                setLoading(false);
            }
        }
    }, []);

    const remember = useCallback(async (deviceId: string | null) => {
        setBusy(true);

        try {
            const updated = await selectDocumentReader(browserFetch, deviceId);

            if (!updated.ok) {
                return false;
            }

            if (live.current) {
                // A confirmed selection is the service telling us what it just decided, so it is
                // taken as-is rather than waiting for the next reconcile to observe it.
                setReaders(updated.value);
                setChosen(deviceId);
            }

            return true;
        } finally {
            if (live.current) {
                setBusy(false);
            }
        }
    }, []);

    const chooseChip = useCallback(async (deviceId: string | null) => {
        setBusy(true);

        try {
            const updated = await selectChipReader(browserFetch, deviceId);

            if (!updated.ok) {
                return false;
            }

            if (live.current) {
                // A confirmed selection is the service telling us what it just decided, so it is
                // taken as-is rather than waiting for the next reconcile to observe it.
                setChipReaders(updated);
                setChosenChip(deviceId);
            }

            return true;
        } finally {
            if (live.current) {
                setBusy(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        void reload();

        // Bounded reconciliation rather than a poll loop: it exists so a launcher left open notices
        // a scanner being plugged in without the operator reaching for refresh.
        const timer = window.setInterval(() => void reload(), RECONCILE_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [enabled, reload]);

    const middleware = useMemo(
        () => (status?.state === 'ready' ? parseEidMiddleware(status.health.eidMiddleware) : null),
        [status],
    );
    const chip = useMemo(
        () => deriveChipAvailability(status, chipReaders, middleware),
        [status, chipReaders, middleware],
    );
    const optical = useMemo(() => deriveOpticalAvailability(status, readers), [status, readers]);
    const device = useMemo(() => chooseOpticalDevice(readers, chosen), [readers, chosen]);
    const chipDevice = useMemo(
        () => chooseChipDevice(chipReaders?.ok === true ? chipReaders.value : null, chosenChip),
        [chipReaders, chosenChip],
    );

    return {
        status,
        chip,
        optical,
        preferred: defaultIdentityProvider(chip, optical),
        device,
        chipDevice,
        middleware,
        middlewareUncertain: middlewareIsUncertain(middleware),
        loading,
        busy,
        choose: setChosen,
        chooseChip,
        remember,
        reload,
    };
}
